import { useState, useEffect, useRef } from 'react';
import { apiGet } from '../../lib/api';

/** GRN fix #1 — free-typed shop / vendor name with optional Vendor Master suggestions.
 *  Picking a suggestion sets vendorId + name; typing again clears vendorId but keeps the text.
 *  Pending (not empanelled) vendors may be picked; blacklisted / suspended are hidden.
 *  No warning when the name is not in the master — this is NOT the Procurement picker. */
export function VendorNameInput({ name, vendorId, onChange, testid = 'vendor-name-input', placeholder = 'e.g. Shree Hardware, Nashik Road', compact = false }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open || !(name || '').trim() || vendorId) { setRows([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      apiGet('/vendors/search', { q: name.trim() })
        .then(list => setRows((Array.isArray(list) ? list : []).filter(v => !v.is_blacklisted && !['blacklisted', 'suspended'].includes(String(v.status || '').toLowerCase())).slice(0, 25)))
        .catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [name, open, vendorId]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={boxRef} className="relative">
      <input value={name || ''} data-testid={testid} placeholder={placeholder} autoComplete="off"
        onChange={(e) => { onChange({ name: e.target.value, vendorId: '' }); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className={`w-full border rounded-lg ${compact ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'}`} />
      {vendorId && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" data-testid={`${testid}-linked`}>in Vendor Master</span>}
      {open && rows.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-suggestions`}>
          {rows.map(v => (
            <button type="button" key={v.id} onClick={() => { onChange({ name: v.name, vendorId: v.id, vendor: v }); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b last:border-0" data-testid={`${testid}-suggestion-${v.id}`}>
              <span className="font-medium">{v.name}</span>
              {v.vendor_code && <span className="text-slate-400 ml-1 font-mono">{v.vendor_code}</span>}
              {v.empanelment_status && v.empanelment_status !== 'approved' && <span className="ml-1 text-[10px] text-amber-700">({v.empanelment_status})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
